import { Platform } from 'react-native';
import { Theme } from '@aws-amplify/ui-react-native';

export const colors = {
    'Primary-Reg': '#FFC319',
    'Primary-Reg-Dark': '#F69301',
    Primary: '#4D6692',
    'Primary-Light': '#9DA8B7',
    Background: '#E8ECEF',
    White: '#FFFFFF',
    Black: '#000000',
    Dividers: '#D5D5D5',
    Dark: '#171717',
    Grey: '#737373',
    'Grey-Light': '#AAAAAA',
    'Grey-Light2': '#F2F2F2',
    Alert: '#ED3C3C',
    Success: '#0B9442',
    'Primary-Button-Default': '#FFC319',
    'Primary-Button-Hover': '#FED560',
    'Primary-Button-Press': '#DFA44E',
    'Primary-Button-Inactive': '#8D8D8D',
    'Secondary-Button-Default': '#FFFFFF',
    'Secondary-Button-Hover': '#FFFFFF',
    'Secondary-Button-Press': '#F3F3F3',
    'Secondary-Button-Inactive': '#FFFFFF',
    'Alert-Button-Default': '#ED3C3C',
    'Alert-Button-Hover': '#FF725A',
    'Alert-Button-Press': '#A12F1C',
    'Alert-Button-Inactive': '#676767',
    'Dark-Text': '#171717',
    'Gray-Text': '#737373',
    'Light-Text': '#FFFFFF',
    'Inactive-Text': '#8D8D8D',
    'White-Text': '#FFFFFF',
    'Secondary-Button-Hover-Border': '#5E5E5E',
    'Drag-Indicator': '#020202',
    'Tab-Gradient-Start': '#CED2D6',
    'Tab-Gradient-End': '#FCFCFD',
    'Spinner-Gradient-1': '#F69301',
    'Spinner-Gradient-2': '#FFC319',
    'Progress-Bar-Gradient-1': '#F6B934',
    'Progress-Bar-Gradient-2': '#F69504',
    'Spinner-Track': '#ECECEC',
    'Drawer-Background': '#2B2D38',
    'Pro-Drawer-Background': '#23324C',
    'Drawer-Item-Active': '#424551',
    'Pro-Drawer-Item-Active': '#9DA8B7',
    'Drawer-Button': '#1E202D',
    'File-Picker-Border': '#D3D3D3',
    'Chat-Divider': '#C3C3C3',
    'Chat-Input-Button-Default': '#76C65B',
    'Chat-Input-Button-Hover': '#08943F',
    'Chat-Input-Button-Inactive': '#D9D9D9',
    'Chat-User-Message': '#DEF8D5',
    'Chat-Model-Message': '#DAF4FF',
    'Avatar-border': '#D4D4D4',
    'Paper-Border': '#D4D4D4',
    'Text-Link': '#E79000',
    Shadow: '#000000',
    'Event-Filter-All': '#F0F0F0',
    'Event-Filter-Visit': '#FAC884',
    'Event-Filter-Record-Update': '#98CCEA',
    'Event-Filter-Collaboration': '#A3D9A5',
    'Event-Filter-Profile': '#C5A1E6',
};

export type Shadow = {
    shadowOffset: { width: number; height: number };
    shadowOpacity: number;
    shadowRadius: number;
    shadowColor: string;
    elevation?: number;
};

export type ShadowType = 'hover' | 'default' | 'press' | 'card';

export const shadows: Record<ShadowType, Shadow> = {
    hover: {
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.2, // 33 in hexadecimal is roughly 20% in decimal
        shadowRadius: 1,
        shadowColor: colors.Black,
        elevation: 3, // for Android
    },
    default: {
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.14, // 24 in hexadecimal is roughly 14% in decimal
        shadowRadius: 2,
        shadowColor: colors.Black,
        elevation: 2, // for Android
    },
    press: {
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.12, // 1F in hexadecimal is roughly 12% in decimal
        shadowRadius: 5,
        shadowColor: colors.Black,
        elevation: 1, // for Android
    },
    card: {
        shadowColor: colors['Primary-Light'],
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 1,
        shadowRadius: 0,
        elevation: 2,
    },
};

export type Color = keyof typeof colors;

const webTheme: Theme = {
    components: {
        textField: {
            container: {
                alignSelf: 'center',
                width: '100%',
            },
            fieldContainer: {
                width: '100%',
            },
        },
        label: {
            text: {
                fontFamily: 'OverpassRegular',
                fontSize: 14,
                lineHeight: 18.2,
                letterSpacing: 0,
                color: colors['Dark-Text'],
            },
        },
        button: {
            containerPrimary: {
                backgroundColor: colors['Primary-Button-Default'],
                width: '100%',
                alignSelf: 'center',
                opacity: 1,
                // color: colors['Dark-Text'],
                borderRadius: 5,
                ...shadows.default,
            },
            disabled: {
                backgroundColor: colors['Grey-Light'],
                opacity: 1,
                // color: colors['Grey'],
                shadowOpacity: 0,
            },
            pressed: {
                opacity: 0.8,
                ...shadows.press,
            },
            containerDefault: {
                width: '100%',
                alignSelf: 'center',
                borderRadius: 5,
                borderWidth: 1,
                borderColor: colors['Dark-Text'],
                backgroundColor: colors['White'],
                ...shadows.default,
                padding: 0,
            },
            containerLink: {
                width: '100%',
                paddingTop: 2,
                paddingBottom: 2,
            },
            textPrimary: {
                color: 'black',
                fontFamily: 'OverpassSemibold',
                fontSize: 14,
                lineHeight: 18.2,
                letterSpacing: 0,
                // fontSynthesis: 'style',
            },
            textLink: {
                fontFamily: 'OverpassSemibold',
                // fontSynthesis: 'style',
            },
            text: {
                color: colors['Dark-Text'],
                fontFamily: 'OverpassSemibold',
                fontSize: 14,
                lineHeight: 18.2,
                letterSpacing: 0,
                // fontSynthesis: 'style',
            },
        },
        errorMessage: {
            container: {
                marginLeft: 0,
                marginRight: 0,
                backgroundColor: colors['Alert'],
                borderRadius: 5,
            },
            label: {
                fontFamily: 'OverpassSemibold',
                fontSize: 14,
                lineHeight: 18.2,
                letterSpacing: 0,
                color: colors['White'],
                textAlign: 'center',
            },
            icon: {
                display: 'none',
            },
        },
    },
};

export const amplifyUITheme: Theme = {
    components: Platform.select({
        web: webTheme.components,
        default: webTheme.components,
    }),
    tokens: {
        colors: {
            background: {
                primary: 'transparent',
            },
            /*
            border: {
                primary: primaryRecordTheme.colors.primaryRecordCustom.cardBorder,
            },
            */
            primaryRecordPrimary: {
                10: '#293e5c',
                20: '#243752',
                40: '#1b293d',
                60: '#121c29',
                80: '#090e14',
                90: '#04070a',
                100: '#000000',
            },
            brand: {
                primary: {
                    10: '{colors.primaryRecordPrimary.10}',
                    20: '{colors.primaryRecordPrimary.20}',
                    40: '{colors.primaryRecordPrimary.40}',
                    60: '{colors.primaryRecordPrimary.60}',
                    80: '{colors.primaryRecordPrimary.80}',
                    90: '{colors.primaryRecordPrimary.90}',
                    100: '{colors.primaryRecordPrimary.100}',
                },
            },
        },
    },
};

export const zIndex = {
    prompt: 1000,
    drawer: 900,
    drawerBackdrop: 899,
    notification: 1000000,
};

export const isLightColor = (hexColor: string) => {
    // Remove the # if it exists
    const hex = hexColor.replace('#', '');

    // Convert hex to RGB
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Calculate the perceived brightness using the formula
    // (0.299*R + 0.587*G + 0.114*B)
    const brightness = r * 0.299 + g * 0.587 + b * 0.114;

    // Return true if the color is light (brightness > 155)
    return brightness > 155;
};
